import React, { ReactNode, useEffect, useState } from "react";
import { useLocation, Navigate } from "react-router-dom";

import { AUTH_USER } from "constants/constants";
import { SLAPSHOT_LOGIN_URL, SLAPSHOT_REGISTER_URL } from "constants/urls";
import { refreshUser } from "services/UserService";
import { getUser } from "utils/helper";

interface AuthWrapperProps {
  children: ReactNode;
}

const AuthWrapper: React.FC<AuthWrapperProps> = ({ children }) => {
  const location = useLocation();
  const user = getUser();
  const [isCheckingAuth, setIsCheckingAuth] = useState(!user);
  const [shouldRedirect, setShouldRedirect] = useState(false);

  useEffect(() => {
    if (!user) {
      // If no user in localStorage, check if we have an active session (including impersonation)
      refreshUser()
        .then((data) => {
          if (data.user || data.impersonating_user) {
            setIsCheckingAuth(false);
            setShouldRedirect(false);
            if (data.user && !getUser()) {
              localStorage.setItem(AUTH_USER, JSON.stringify(data.user));
            }
          } else {
            setIsCheckingAuth(false);
            setShouldRedirect(true);
          }
        })
        .catch(() => {
          setIsCheckingAuth(false);
          setShouldRedirect(true);
        });
    }
  }, [user]);

  // If URL begins with /admin/, redirect to admin site
  if (location.pathname.startsWith("/admin/")) {
    window.location.href = location.pathname + location.search;
    return null;
  }

  if (isCheckingAuth) {
    return null;
  }

  if (user || !shouldRedirect) {
    return children;
  }

  // Check URL parameter for redirect preference
  const urlParams = new URLSearchParams(location.search);
  const redirectParam = urlParams.get("redirect");

  // If user is not authenticated, redirect to appropriate auth page
  const currentPath = location.pathname + location.search;
  const nextParam = `?next=${encodeURIComponent(currentPath)}`;

  const redirectPath =
    redirectParam === "register"
      ? SLAPSHOT_REGISTER_URL + nextParam
      : SLAPSHOT_LOGIN_URL + nextParam;

  return <Navigate to={redirectPath} replace />;
};

export default AuthWrapper;
